import { useState, useRef } from "react";
import {
  downloadConsumerTemplate,
  bulkUploadConsumer,
} from "../../../services/panel/panelServices";
import toast from "../../../utils/toast";

const TEMPLATE_NAME = "SampleConsumerAccount.xlsx";

// A failed blob request still carries the API's JSON ({ statuscode, msg }) as a Blob
const readBlobMsg = async (blob) => {
  try {
    return JSON.parse(await blob.text())?.msg;
  } catch {
    return null;
  }
};

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function AddCABulk() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const blob = await downloadConsumerTemplate();

      // API answered with JSON (e.g. statuscode -1) instead of the file
      if (blob?.type?.includes("json")) {
        toast.error((await readBlobMsg(blob)) || "Failed to download template.");
        return;
      }
      saveBlob(blob, TEMPLATE_NAME);
      toast.success("Template downloaded.");
    } catch (err) {
      const data = err.response?.data;
      const msg = data instanceof Blob ? await readBlobMsg(data) : data?.msg;
      toast.error(msg || "Failed to download template.");
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0] || null;
    setFileError("");
    if (picked && !/\.(xlsx|xls)$/i.test(picked.name)) {
      setFile(null);
      setFileError("Upload an Excel file (.xlsx or .xls).");
      return;
    }
    setFile(picked);
  };

  const handleUpload = async () => {
    if (!file) {
      setFileError("Choose the filled template to upload.");
      return;
    }
    setUploading(true);
    try {
      const res = await bulkUploadConsumer(file);
      console.log("BulkUpload", res);

      if (res?.statuscode === 1) {
        toast.success(res?.msg || "Accounts uploaded.");
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
      } else {
        toast.error(res?.msg || "Failed to upload accounts.");
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to upload accounts.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="screen active" id="screen-ca-bulk">
        <div className="page-head">
          <div>
            <h1>Add consumer accounts — bulk</h1>
            <div className="ph-sub">
              Upload many accounts at once using the standard template
            </div>
          </div>
        </div>
        <div className="grid-2">
          <div className="card">
            <div className="card-title">1. Download template</div>
            <div className="card-sub">
              Biller ID (from the Biller list), consumer number, nickname and
              amount — all required
            </div>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleDownloadTemplate}
              disabled={downloading}
            >
              {downloading ? "Downloading..." : "Download Excel template"}
            </button>
            <div className="card-title" style={{ marginTop: 22 }}>
              2. Upload your file
            </div>
            <div className="field">
              <input
                type="file"
                id="bulk-file"
                ref={fileRef}
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={fileError ? { borderColor: "#dc2626" } : {}}
              />
              {fileError && (
                <span style={{ color: "#dc2626", fontSize: 11 }}>
                  {fileError}
                </span>
              )}
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Upload & validate"}
            </button>
            {uploading && (
              <div style={{ marginTop: 16 }}>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--text-faint)",
                  }}
                >
                  Validating rows…
                </div>
              </div>
            )}
          </div>
          {/* Import summary card — bind once the BulkUpload response is known */}
        </div>
      </div>
    </>
  );
}
